/*
 * The launcher's intro card: a short "what this widget is for" note, peeked
 * once for a first-time visitor and available on hover after that.
 *
 * Same closed-shadow-root isolation as fab.css: selectors stay inside the
 * shadow tree and :host pins inherited text properties against host-page
 * styling. All copy is admin-authored and rendered through textContent.
 */

:host {
	--ink: #14141f;
	--ink-muted: #3a3b47;
	--ink-subtle: #6b6c80;
	--accent: hsl(260deg 100% 69%);
	--hairline: rgb(20 20 31 / 10%);

	position: fixed;
	right: 26px;
	bottom: 104px;
	z-index: 2147483645;
	width: min(316px, calc(100vw - 52px));
	font-family:
		system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial,
		sans-serif;
	font-size: 14px;
	line-height: 1.45;
	text-align: start;
	color-scheme: light;
}

:host([data-position="left"]) {
	right: auto;
	left: 26px;
}

/* Tab launchers sit against the edge rather than above a corner bubble, so
   the card docks beside the tab instead of over it. */
:host([data-mode="vertical-tab"]) {
	top: 20px;
	right: 64px;
	bottom: auto;
}

:host([data-mode="vertical-tab"][data-position="left"]) {
	right: auto;
	left: 64px;
}

:host([data-mode="horizontal-tab"]) {
	bottom: 62px;
}

:host([data-hidden]) {
	display: none;
}

/* Out of sight while the launcher is dragged, so it never floats apart from
   it. Visibility only – whether it shows at all is decided elsewhere. */
:host([data-launcher-moving]) {
	visibility: hidden;
}

.card {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 16px;
	color: var(--ink);
	background: #fff;
	border: 1.5px solid rgb(123 63 255 / 25%);
	border-radius: 16px;
	box-shadow:
		0 4px 12px rgb(20 20 31 / 8%),
		0 18px 44px rgb(123 63 255 / 16%);
}

@media (prefers-reduced-motion: no-preference) {
	:host(:not([data-hidden])) .card {
		animation: weloop-intro-in 220ms cubic-bezier(0.16, 1, 0.3, 1);
	}

	@keyframes weloop-intro-in {
		from {
			opacity: 0;
			transform: translateY(8px);
		}
	}
}

/* An invisible bridge across the gap between the card and the launcher, so a
   pointer travelling down to the bubble (or up to the button) is never over
   neither. Without it the card retracts mid-journey and the button below it
   cannot be reached. Sized to the 14px gap plus margin. */
.card::before {
	position: absolute;
	right: 0;
	bottom: -20px;
	left: 0;
	height: 20px;
	content: "";
}

:host([data-mode="vertical-tab"]) .card::before {
	top: 0;
	right: -20px;
	bottom: 0;
	left: auto;
	width: 20px;
	height: auto;
}

:host([data-mode="vertical-tab"][data-position="left"]) .card::before {
	right: auto;
	left: -20px;
}

/* Caret pointing back at the launcher, so the card reads as belonging to the
   bubble rather than floating loose on the host page. */
.card::after {
	position: absolute;
	right: 26px;
	bottom: -7px;
	width: 12px;
	height: 12px;
	content: "";
	background: #fff;
	border-right: 1.5px solid rgb(123 63 255 / 25%);
	border-bottom: 1.5px solid rgb(123 63 255 / 25%);
	transform: rotate(45deg);
}

:host([data-position="left"]) .card::after {
	right: auto;
	left: 26px;
}

/* The tab modes dock to the side, so the caret would point at nothing. */
:host([data-mode="vertical-tab"]) .card::after {
	display: none;
}

.head {
	display: flex;
	gap: 10px;
	align-items: flex-start;
}

.spark {
	flex: none;
	width: 10px;
	height: 10px;
	margin-top: 6px;
	background: linear-gradient(
		135deg,
		hsl(260deg 100% 69%),
		hsl(280deg 100% 73%)
	);
	border-radius: 50%;
}

.headText {
	flex: 1;
	min-width: 0;
}

.title {
	margin: 0;
	font-size: 14.5px;
	font-weight: 700;
	letter-spacing: -0.01em;
	overflow-wrap: break-word;
}

.message {
	margin: 2px 0 0;
	font-size: 12.5px;
	color: var(--ink-subtle);
	overflow-wrap: break-word;
}

/* 26px visual, 44px hit area through the padding – the WCAG tap-target floor
   without a chunky control next to the title. */
.dismiss {
	position: relative;
	display: inline-flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	padding: 0;
	margin: -6px -6px 0 0;
	font-size: 15px;
	line-height: 1;
	color: var(--ink-subtle);
	cursor: pointer;
	background: transparent;
	border: 0;
	border-radius: 50%;
}

.dismiss::before {
	position: absolute;
	width: 44px;
	height: 44px;
	content: "";
	transform: translate(-9px, -9px);
}

.dismiss:hover {
	color: var(--ink);
	background: rgb(20 20 31 / 6%);
}

.bullets {
	display: flex;
	flex-direction: column;
	gap: 7px;
	padding: 0;
	margin: 0;
	list-style: none;
}

.bullet {
	display: flex;
	gap: 9px;
	align-items: center;
	padding: 7px 10px;
	font-size: 12.5px;
	color: var(--ink-muted);
	overflow-wrap: break-word;
	background: #f7f7fb;
	border: 1px solid rgb(20 20 31 / 6%);
	border-radius: 10px;
}

/* Tints match the reply preview's classification avatars. */
.mark {
	display: grid;
	flex: none;
	place-items: center;
	width: 22px;
	height: 22px;
	color: #7b3fff;
	background: rgb(123 63 255 / 12%);
	border-radius: 7px;
}

.mark svg {
	width: 13px;
	height: 13px;
}

.mark[data-tone="it"] {
	color: #1a6fbf;
	background: rgb(61 157 255 / 14%);
}

.mark[data-tone="bug"] {
	color: #b72c5a;
	background: rgb(224 74 124 / 13%);
}

.mark[data-tone="support"] {
	color: #7a5500;
	background: rgb(232 154 0 / 16%);
}

.cta {
	align-self: flex-start;
	padding: 9px 16px;
	font-family: inherit;
	font-size: 13px;
	font-weight: 600;
	color: #fff;
	cursor: pointer;
	background: linear-gradient(
		135deg,
		hsl(260deg 100% 69%),
		hsl(272deg 100% 68%)
	);
	border: 0;
	border-radius: 999px;
	box-shadow: 0 6px 16px rgb(123 63 255 / 30%);
}

.cta:hover {
	filter: brightness(1.05);
}

/* --- Brand curtain ---------------------------------------------------------
   The first time a self-opening card shows on a page, a block of the project
   color carrying its name covers it, then lifts like a curtain while
   the content rises in. The host sets `data-intro` for that one showing. The
   curtain rounds its own corners: the card cannot clip, its tail and hover
   bridge sit outside it. Kept terse – every host page pays for these bytes. */
.curtain {
	position: absolute;
	inset: 0;
	display: none;
	align-items: center;
	justify-content: center;
	/* Any project name has to fit: it wraps, then the curtain clips it. */
	overflow: hidden;
	font-size: 20px;
	font-weight: 800;
	color: #fff;
	background: var(--accent);
	border-radius: 14.5px;
}

@media (prefers-reduced-motion: no-preference) {
	:host([data-intro]) .curtain {
		display: flex;
		animation: weloop-curtain 520ms ease-in-out 1s both;
	}

	/* No fill mode: the curtain covers the content until its delay ends. */
	:host([data-intro]) .card > :not(.curtain) {
		animation: weloop-intro-in 220ms cubic-bezier(0.16, 1, 0.3, 1) 1s;
	}

	@keyframes weloop-curtain {
		to {
			clip-path: inset(0 0 100%);
		}
	}
}
